import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, ProtectedRoute } from '@es-tools/shared';
import Login from './pages/Login';
import BrandHome from './pages/BrandHome';
import AppPlaceholder from './pages/AppPlaceholder';
import ReviewsPage from './pages/Reviews';
import HolyShipped from './pages/HolyShipped';
import AutomationLibrary from './pages/AutomationLibrary';
import DataPlatformPage from './pages/DataPlatform';

const DEFAULT_ALLOWED_EMAIL_DOMAINS = [
  'hirehoratio.co',
  'mengotomars.com',
  'hollowsocks.com',
  'paradigmcapital.xyz',
];

const parseAllowedDomains = (rawValue?: string): string[] => {
  const source = rawValue ?? DEFAULT_ALLOWED_EMAIL_DOMAINS.join(',');
  return source
    .split(',')
    .map((domain) => domain.trim().toLowerCase())
    .filter((domain) => domain.length > 0);
};

const allowedDomains = parseAllowedDomains(import.meta.env.VITE_ALLOWED_EMAIL_DOMAINS);

type AppProps = {
  authApiBaseUrl: string;
};

function App({ authApiBaseUrl }: AppProps) {
  const devBypassEnabled = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';

  return (
    <AuthProvider
      allowDevBypass={devBypassEnabled}
      allowedDomains={allowedDomains}
      authApiBaseUrl={authApiBaseUrl}
    >
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <BrandHome />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reviews"
            element={
              <ProtectedRoute>
                <ReviewsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/holy-shipped"
            element={
              <ProtectedRoute>
                <HolyShipped />
              </ProtectedRoute>
            }
          />
          <Route
            path="/holy-shipped/week/:weekNumber"
            element={
              <ProtectedRoute>
                <HolyShipped />
              </ProtectedRoute>
            }
          />
          <Route
            path="/automation-library"
            element={
              <ProtectedRoute>
                <AutomationLibrary />
              </ProtectedRoute>
            }
          />
          <Route
            path="/data-platform"
            element={
              <ProtectedRoute>
                <DataPlatformPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/analytics"
            element={
              <ProtectedRoute>
                <AppPlaceholder appName="Analytics Dashboard" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/inventory"
            element={
              <ProtectedRoute>
                <AppPlaceholder appName="Inventory Manager" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/campaigns"
            element={
              <ProtectedRoute>
                <AppPlaceholder appName="Marketing Campaigns" />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
